import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Button,
  Tabs,
  Tab,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Save,
  Download,
  Search,
  Clear,
  Comment as CommentIcon,
  ViewList,
  ViewModule,
} from '@mui/icons-material';
import apiService from '../../services/api';
import ExportDialog from '../export/ExportDialog';
import VersionHistory from '../version/VersionHistory';
import CommentsPanel from '../comments/CommentsPanel';
import CommentThread from '../comments/CommentThread';
import {
  chunkSegmentsByDuration,
  mergeSegmentText,
  splitChunkIntoSegments,
  formatChunkTimestamp,
} from '../../utils/segmentChunking';

/**
 * Enhanced TranscriptEditor Component with Chunking Support
 *
 * Interactive transcript editor that can display segments either individually
 * or grouped into time-based chunks for easier editing.
 * 
 * Features:
 * - Toggle between chunked (~5 minute blocks) and segment view
 * - Editable transcript chunks with proper timestamps
 * - Auto-save with debouncing
 * - In-transcript search
 * - Export functionality
 * - Keyboard shortcuts (Ctrl+S to save)
 */
const TranscriptEditorChunked = ({ transcriptId, audioFileId }) => {
  // Refs
  const autoSaveTimerRef = useRef(null);

  // State
  const [transcript, setTranscript] = useState(null);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [matchingSegmentIds, setMatchingSegmentIds] = useState(new Set());

  // Version control & comments state
  const [currentTab, setCurrentTab] = useState(0);
  const [segmentComments, setSegmentComments] = useState({});
  const [showCommentsFor, setShowCommentsFor] = useState(new Set());
  const [commentsKey, setCommentsKey] = useState(0);

  // Chunking state
  const [viewMode, setViewMode] = useState('chunked'); // 'chunked' or 'segments'
  const [chunks, setChunks] = useState([]);
  const [chunkDuration, setChunkDuration] = useState(300); // 5 minutes default
  const [editedChunks, setEditedChunks] = useState({});

  // Load transcript data
  useEffect(() => {
    const loadTranscript = async () => {
      try {
        setLoading(true);
        const data = await apiService.getTranscription(transcriptId);
        setTranscript(data);
        setSegments(data.segments || []);
        setLoading(false);
      } catch (err) {
        setError('Failed to load transcript: ' + err.message);
        setLoading(false);
      }
    };

    if (transcriptId) {
      loadTranscript();
    }
  }, [transcriptId]);

  // Generate chunks when segments change or view mode changes
  useEffect(() => {
    if (viewMode === 'chunked' && segments.length > 0) {
      const newChunks = chunkSegmentsByDuration(segments, chunkDuration);
      setChunks(newChunks);
      
      // Initialize edited chunks text
      const initialEditedChunks = {};
      newChunks.forEach((chunk, index) => {
        if (!editedChunks[index]) {
          initialEditedChunks[index] = mergeSegmentText(chunk.segments);
        }
      });
      setEditedChunks(prev => ({ ...initialEditedChunks, ...prev }));
    }
  }, [segments, viewMode, chunkDuration]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Ctrl+S or Cmd+S: save
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [hasUnsavedChanges]);

  // Auto-save with debouncing
  useEffect(() => {
    if (hasUnsavedChanges && !saving) {
      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      // Set new timer for auto-save after 3 seconds of inactivity
      autoSaveTimerRef.current = setTimeout(() => {
        handleSave(true);
      }, 3000);
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, segments, editedChunks]);

  // Handle view mode change
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      // If switching from chunked to segments, apply any chunk edits first
      if (viewMode === 'chunked' && newMode === 'segments') {
        applyChunkEditsToSegments();
      }
      setViewMode(newMode);
    }
  };

  // Apply chunk edits back to segments
  const applyChunkEditsToSegments = () => {
    let updatedSegments = [...segments];
    let segmentIndex = 0;

    chunks.forEach((chunk, chunkIndex) => {
      if (editedChunks[chunkIndex] !== undefined) {
        const editedText = editedChunks[chunkIndex];
        const chunkSegments = chunk.segments;
        const splitSegments = splitChunkIntoSegments(editedText, chunkSegments);

        // Update the segments with the split text
        splitSegments.forEach((splitSeg, i) => {
          if (segmentIndex < updatedSegments.length) {
            updatedSegments[segmentIndex] = {
              ...updatedSegments[segmentIndex],
              ...splitSeg,
            };
            segmentIndex++;
          }
        });
      } else {
        segmentIndex += chunk.segments.length;
      }
    });

    setSegments(updatedSegments);
  };

  // Segment editing (for segment view)
  const handleSegmentTextChange = (index, newText) => {
    const updatedSegments = [...segments];
    updatedSegments[index] = {
      ...updatedSegments[index],
      text: newText,
    };
    setSegments(updatedSegments);
    setHasUnsavedChanges(true);
    setSaveSuccess(false);
  };

  // Chunk editing (for chunked view)
  const handleChunkTextChange = (chunkIndex, newText) => {
    setEditedChunks(prev => ({
      ...prev,
      [chunkIndex]: newText,
    }));
    setHasUnsavedChanges(true);
    setSaveSuccess(false);
  };

  // Save transcript
  const handleSave = async (isAutoSave = false) => {
    try {
      setSaving(true);

      // Apply chunk edits to segments if in chunked mode
      if (viewMode === 'chunked') {
        applyChunkEditsToSegments();
      }

      // Rebuild full text from segments
      const fullText = segments.map(seg => seg.text).join(' ');

      await apiService.updateTranscription(transcriptId, {
        segments: segments,
        full_text: fullText,
        create_version: false, // Auto-save doesn't create versions
      });

      setHasUnsavedChanges(false);
      setSaving(false);
      setSaveSuccess(true);

      // Hide success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save: ' + err.message);
      setSaving(false);
    }
  };

  // Search in transcript
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setMatchingSegmentIds(new Set());
      return;
    }

    try {
      setSearchLoading(true);
      const results = await apiService.searchInTranscript(transcriptId, searchQuery);
      setSearchResults(results);

      // Extract segment IDs that have matches
      const matchingIds = new Set(
        results.matches
          .filter(match => match.segment_id !== null)
          .map(match => match.segment_id)
      );
      setMatchingSegmentIds(matchingIds);
      setSearchLoading(false);
    } catch (err) {
      setError('Search failed: ' + err.message);
      setSearchLoading(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setMatchingSegmentIds(new Set());
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Load comments for a segment
  const loadSegmentComments = async (segmentIndex) => {
    try {
      const comments = await apiService.getSegmentComments(transcriptId, segmentIndex);
      setSegmentComments(prev => ({
        ...prev,
        [segmentIndex]: comments,
      }));
    } catch (err) {
      console.error('Failed to load segment comments:', err);
    }
  };

  // Toggle comment visibility for a segment
  const toggleComments = (segmentIndex) => {
    const newSet = new Set(showCommentsFor);
    if (newSet.has(segmentIndex)) {
      newSet.delete(segmentIndex);
    } else {
      newSet.add(segmentIndex);
      // Load comments if not already loaded
      if (!segmentComments[segmentIndex]) {
        loadSegmentComments(segmentIndex);
      }
    }
    setShowCommentsFor(newSet);
  };

  // Handle comment added/updated
  const handleCommentChange = (segmentIndex) => {
    loadSegmentComments(segmentIndex);
    setCommentsKey(prev => prev + 1);
  };

  // Handle version change
  const handleVersionChange = () => {
    window.location.reload();
  };

  // Handle segment click from CommentsPanel
  const handleSegmentClick = (segmentIndex) => {
    setCurrentTab(0);
    setViewMode('segments'); // Switch to segment view to show the specific segment
    setTimeout(() => {
      const element = document.getElementById(`segment-${segmentIndex}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Tabs for different views */}
      <Paper elevation={2} sx={{ mb: 2 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label="Transcript" />
          <Tab label="Version History" />
          <Tab label="Comments" />
        </Tabs>
      </Paper>

      {/* Transcript Editor Tab */}
      {currentTab === 0 && (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              Transcript
            </Typography>
            {transcript && (
              <Chip
                label={viewMode === 'chunked' ? `${chunks.length} chunks` : `${segments.length} segments`}
                size="small"
              />
            )}
            
            {/* View Mode Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
            >
              <ToggleButton value="chunked">
                <Tooltip title="Chunked View (~5 minute blocks)">
                  <ViewModule />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="segments">
                <Tooltip title="Segment View (individual utterances)">
                  <ViewList />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Save and Export Buttons */}
          <Box display="flex" alignItems="center" gap={1}>
            {saveSuccess && (
              <Chip
                label="Saved"
                color="success"
                size="small"
              />
            )}
            {hasUnsavedChanges && !saveSuccess && (
              <Chip
                label="Unsaved changes"
                color="warning"
                size="small"
              />
            )}
            <Tooltip title="Save (Ctrl+S)">
              <IconButton
                onClick={() => handleSave(false)}
                color="primary"
                disabled={saving || !hasUnsavedChanges}
              >
                {saving ? <CircularProgress size={24} /> : <Save />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Export transcript">
              <Button
                variant="outlined"
                size="small"
                startIcon={<Download />}
                onClick={() => setExportDialogOpen(true)}
                disabled={!segments || segments.length === 0}
              >
                Export
              </Button>
            </Tooltip>
          </Box>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search in transcript..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            fullWidth
            disabled={segments.length === 0}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            disabled={!searchQuery.trim() || searchLoading || segments.length === 0}
            sx={{ minWidth: 100 }}
          >
            {searchLoading ? <CircularProgress size={20} /> : 'Search'}
          </Button>
          {searchQuery && (
            <IconButton
              size="small"
              onClick={handleClearSearch}
              title="Clear search"
            >
              <Clear />
            </IconButton>
          )}
        </Box>

        {/* Search Results Summary */}
        {searchResults && (
          <Alert
            severity={searchResults.total > 0 ? 'success' : 'info'}
            sx={{ mb: 2 }}
            onClose={handleClearSearch}
          >
            {searchResults.total > 0
              ? `Found ${searchResults.total} match${searchResults.total === 1 ? '' : 'es'} for "${searchResults.query}"`
              : `No matches found for "${searchResults.query}"`
            }
          </Alert>
        )}

        {segments.length === 0 ? (
          <Alert severity="info">
            No transcript segments available. Transcription may still be processing.
          </Alert>
        ) : (
          <Box>
            {/* Chunked View */}
            {viewMode === 'chunked' && chunks.map((chunk, chunkIndex) => {
              const chunkStartTime = chunk.startTime || 0;
              const chunkEndTime = chunk.endTime || 0;
              const isFirstSegmentInChunk = chunk.segments[0];
              const hasMatchingSegments = chunk.segments.some((seg, i) => 
                matchingSegmentIds.has(segments.indexOf(seg))
              );

              return (
                <Box
                  key={chunkIndex}
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: hasMatchingSegments ? '2px solid' : '1px solid',
                    borderColor: hasMatchingSegments ? 'primary.main' : 'divider',
                  }}
                >
                  {/* Chunk Header with Timestamp Range */}
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Chip
                      label={`${formatChunkTimestamp(chunkStartTime)} - ${formatChunkTimestamp(chunkEndTime)}`}
                      color="primary"
                      size="small"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {chunk.segments.length} segments, {Math.round(chunk.duration)} seconds
                    </Typography>
                  </Box>

                  {/* Editable Chunk Text */}
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={20}
                    value={editedChunks[chunkIndex] || mergeSegmentText(chunk.segments)}
                    onChange={(e) => handleChunkTextChange(chunkIndex, e.target.value)}
                    variant="outlined"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.default',
                        fontFamily: 'inherit',
                        fontSize: '1rem',
                        lineHeight: 1.6,
                      },
                    }}
                  />
                </Box>
              );
            })}

            {/* Segment View (Original) */}
            {viewMode === 'segments' && segments.map((segment, index) => {
              const isMatching = matchingSegmentIds.has(index);
              const hasComments = segmentComments[index] && segmentComments[index].length > 0;
              const showComments = showCommentsFor.has(index);

              return (
                <Box
                  key={index}
                  id={`segment-${index}`}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'transparent',
                    border: isMatching ? '2px solid' : '1px solid transparent',
                    borderColor: isMatching ? 'primary.main' : 'transparent',
                    transition: 'background-color 0.3s, border-color 0.3s',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {/* Segment metadata */}
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={formatTime(segment.start)}
                        size="small"
                      />
                      {segment.speaker && (
                        <Chip
                          label={segment.speaker}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {segment.confidence && (
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(segment.confidence * 100)}% confidence
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size="small"
                      startIcon={<CommentIcon />}
                      onClick={() => toggleComments(index)}
                      variant={showComments ? 'contained' : 'outlined'}
                    >
                      {hasComments ? `${segmentComments[index].length} Comments` : 'Add Comment'}
                    </Button>
                  </Box>

                  {/* Editable text */}
                  <TextField
                    fullWidth
                    multiline
                    value={segment.text}
                    onChange={(e) => handleSegmentTextChange(index, e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'background.paper',
                      },
                    }}
                  />

                  {/* Comment Thread */}
                  <Collapse in={showComments}>
                    <CommentThread
                      transcriptId={transcriptId}
                      segmentIndex={index}
                      comments={segmentComments[index] || []}
                      onCommentAdded={() => handleCommentChange(index)}
                      onCommentDeleted={() => handleCommentChange(index)}
                    />
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>
      )}

      {/* Version History Tab */}
      {currentTab === 1 && (
        <VersionHistory
          transcriptId={transcriptId}
          onVersionChange={handleVersionChange}
          onCompare={(versionAId, versionBId) => {
            alert(`Compare versions ${versionAId} and ${versionBId}`);
          }}
        />
      )}

      {/* Comments Panel Tab */}
      {currentTab === 2 && (
        <CommentsPanel
          key={commentsKey}
          transcriptId={transcriptId}
          segments={segments}
          onSegmentClick={handleSegmentClick}
        />
      )}

      {/* Export Dialog */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        transcriptId={transcriptId}
        segments={segments}
        audioFilename={transcript?.audio_file_id ? `audio_${transcript.audio_file_id}` : 'transcript'}
      />
    </Box>
  );
};

export default TranscriptEditorChunked;